/** @format */

import { inject, Injectable } from "@angular/core";
import { plainToInstance } from "class-transformer";
import { forkJoin, map, Observable, of, tap } from "rxjs";
import { StorageService } from "@domain/shared/storage.service";
import { ApiPagination } from "@domain/other/api-pagination.model";
import { FeedNews } from "@domain/news/project-news.model";
import { ProjectNewsHttpAdapter } from "../../adapters/project/project-news-http.adapter";
import { NewsRepositoryPort } from "@domain/news/port/news.repository.port";
import { EventBus } from "@domain/shared/event-bus";
import { EntityCache } from "@domain/shared/entity-cache";
import {
  AddNews,
  DeleteNews,
  EditNews,
  ToggleLike,
} from "@domain/project/events/project-news.event";
import { LoggedOut } from "@domain/auth/events/logged-out.event";

/** Репозиторий новостей проекта с дедупликацией событий чтения в sessionStorage. */
@Injectable({ providedIn: "root" })
export class ProjectNewsRepository implements NewsRepositoryPort<FeedNews> {
  private readonly projectNewsAdapter = inject(ProjectNewsHttpAdapter);
  private readonly storageService = inject(StorageService);
  private readonly eventBus = inject(EventBus);
  private readonly newsCache = new EntityCache<ApiPagination<FeedNews>>();

  constructor() {
    this.initializeEventListeners();
  }

  private initializeEventListeners(): void {
    this.eventBus.on<AddNews>("AddNews").subscribe({
      next: event => {
        this.newsCache.invalidate(Number(event.payload.projectId));
      },
    });

    this.eventBus.on<DeleteNews>("DeleteNews").subscribe({
      next: event => {
        this.newsCache.invalidate(Number(event.payload.projectId));
      },
    });

    this.eventBus.on<EditNews>("EditNews").subscribe({
      next: event => {
        this.newsCache.invalidate(Number(event.payload.projectId));
      },
    });

    this.eventBus.on<ToggleLike>("ToggleLike").subscribe({
      next: event => {
        this.newsCache.invalidate(Number(event.payload.projectId));
      },
    });

    // ReadNews помечает серверный счётчик "просмотрено" — контент новостей не меняется
    // (текст/файлы/лайки/автор). Инвалидация кеша на него убивала бенефит кеширования:
    // IntersectionObserver на скролле эмитил ReadNews → кеш сбрасывался → следующий
    // вход на проект делал лишний HTTP. Сейчас счётчик "прочитано" обновится при
    // естественной инвалидации (Add/Edit/Delete/Like) или со следующим деплоем.

    this.eventBus.on<LoggedOut>("LoggedOut").subscribe({
      next: () => {
        this.newsCache.clear();
      },
    });
  }

  fetchNews(projectId: string): Observable<ApiPagination<FeedNews>> {
    return this.newsCache.getOrFetch(Number(projectId), () =>
      this.projectNewsAdapter
        .fetchNews(projectId)
        .pipe(map(page => ({ ...page, results: plainToInstance(FeedNews, page.results) }))),
    );
  }

  fetchNewsDetail(projectId: string, newsId: string): Observable<FeedNews> {
    return this.projectNewsAdapter
      .fetchNewsDetail(projectId, newsId)
      .pipe(map(news => plainToInstance(FeedNews, news)));
  }

  addNews(projectId: string, obj: { text: string; files: string[] }): Observable<FeedNews> {
    return this.projectNewsAdapter
      .addNews(projectId, obj)
      .pipe(map(news => plainToInstance(FeedNews, news)));
  }

  readNews(projectId: number, newsIds: number[]): Observable<void[]> {
    const cachedReadNews = this.storageService.getItem<number[]>("readNews", sessionStorage) ?? [];
    const readNews = new Set<number>(cachedReadNews);
    // Повторные просмотры не отправляются на бэк в рамках текущей сессии браузера.
    const unreadIds = newsIds.filter(id => !readNews.has(id));

    if (unreadIds.length === 0) {
      return of([]);
    }

    return forkJoin(
      unreadIds.map(id =>
        this.projectNewsAdapter.setNewsViewed(projectId, id).pipe(
          tap(() => {
            readNews.add(id);
            this.storageService.setItem("readNews", [...readNews], sessionStorage);
          }),
        ),
      ),
    );
  }

  delete(projectId: string, newsId: number): Observable<void> {
    return this.projectNewsAdapter.deleteNews(projectId, newsId);
  }

  toggleLike(projectId: string, newsId: number, state: boolean): Observable<void> {
    return this.projectNewsAdapter.toggleLike(projectId, newsId, state);
  }

  editNews(projectId: string, newsId: number, newsItem: Partial<FeedNews>): Observable<FeedNews> {
    return this.projectNewsAdapter
      .editNews(projectId, newsId, newsItem)
      .pipe(map(news => plainToInstance(FeedNews, news)));
  }
}
